// =============================================================================
// Cola offline (Dexie/IndexedDB) — el corazón del POS offline-first.
// El POS debe vender SIEMPRE, aun sin internet (y durante la contingencia fiscal
// de 72h). Las ventas se escriben primero a esta cola local; un sincronizador
// las envía a Supabase cuando hay red. La idempotencia de pos_finalize_sale
// (por client_uuid) hace que reenviar la misma venta NO duplique nada.
// =============================================================================
import Dexie, { type Table } from "dexie";

/** Una venta encolada para sincronizar (se arma 100% offline). */
export interface QueuedSale {
  clientUuid: string;          // PK e idempotencia (mismo uuid que pos_finalize_sale)
  branchId: string;
  cashSessionId: string | null;
  customerId: string | null;
  lines: QueuedLine[];
  payments: { method: string; amount: number; reference?: string | null }[];
  changeGiven: number;
  total: number;
  emitInvoice: boolean;        // emitir factura electrónica al sincronizar
  createdAt: number;
  status: "pending" | "syncing" | "synced" | "error";
  attempts: number;
  lastError?: string;
  saleId?: string;             // id remoto una vez sincronizada
}

export interface QueuedLine {
  clientUuid: string;          // idempotencia por línea (sync no duplica al reintentar)
  productUnitId: string;
  productId: string;
  qty: number;
  unitPrice: number;
  /** Descuento absoluto en B/. (incluye prorrateo del descuento global). */
  discount?: number;
  name: string;                // snapshot para reimprimir el tiquete offline
  /** Descripción libre cuando el producto es OPEN-ITEM (servicio/ajuste). */
  lineNote?: string;
}

/** Snapshot del catálogo + stock por sucursal, para vender sin red. */
export interface CachedProduct {
  key: string;                 // `${branchId}:${productId}`
  branchId: string;
  productId: string;
  sku: string;
  name: string;
  itbmsRate: string;
  qtyAvailable: number;
  units: { productUnitId: string; factor: number; price: number | null }[];
  barcodes: string[];
  cachedAt: number;
}

class FerreDB extends Dexie {
  queuedSales!: Table<QueuedSale, string>;
  catalog!: Table<CachedProduct, string>;

  constructor() {
    super("ferresaas");
    this.version(1).stores({
      // índices: por status para el sync, por createdAt para orden FIFO
      queuedSales: "clientUuid, status, createdAt, branchId",
      // índices: por sucursal y por sku para búsqueda; multiEntry en barcodes
      catalog: "key, branchId, sku, *barcodes",
    });
  }
}

export const db = new FerreDB();
